/**
 * PoliciesTab — booking policies editor (cancel window, deposits, no-show
 * fees, auto-fill waitlist). One save, applies to all future bookings.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, ShieldCheck, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const NumberField = ({ label, hint, value, onChange, testid, min = 0, max = 100, suffix }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-2">
      <Input
        type="number"
        min={min}
        max={max}
        value={value ?? 0}
        onChange={(e) => onChange(parseInt(e.target.value || '0', 10))}
        data-testid={testid}
      />
      {suffix && <span className="text-xs text-slate-500 whitespace-nowrap">{suffix}</span>}
    </div>
    {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
  </div>
);

const ToggleField = ({ label, hint, checked, onChange, testid }) => (
  <div className="flex items-start justify-between gap-3 py-2">
    <div className="flex-1">
      <Label className="text-sm">{label}</Label>
      {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export default function PoliciesTab() {
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/booking/policy`, auth).then((r) => setP(r.data)).catch(() => {
      toast.error('Failed to load policies');
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/booking/policy`, p, auth);
      setP(data);
      toast.success('Policies saved — applies to all future bookings.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!p) return <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading policies…</div>;

  return (
    <div className="space-y-6" data-testid="policies-tab">
      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
        <ShieldCheck className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
        <div className="text-sm text-emerald-900">
          These policies are shown to clients at booking and enforced when they cancel or reschedule via the magic links in their confirmation emails.
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Client self-service</CardTitle>
        </CardHeader>
        <CardContent>
          <ToggleField
            label="Allow clients to cancel their own booking"
            hint="They'll get a Cancel button in their confirmation email."
            checked={p.allow_self_cancel}
            onChange={(v) => setP({ ...p, allow_self_cancel: v })}
            testid="toggle-allow-cancel"
          />
          <ToggleField
            label="Allow clients to reschedule themselves"
            hint="They'll see a Reschedule button in the same email."
            checked={p.allow_self_reschedule}
            onChange={(v) => setP({ ...p, allow_self_reschedule: v })}
            testid="toggle-allow-reschedule"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Notice windows</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <NumberField
            label="Cancel notice (hours)"
            hint="Clients cancelling inside this window get charged the late fee."
            value={p.cancel_window_hours}
            onChange={(v) => setP({ ...p, cancel_window_hours: v })}
            min={0} max={168} suffix="hours"
            testid="cancel-window-input"
          />
          <NumberField
            label="Reschedule cutoff (hours)"
            hint="Clients cannot reschedule inside this window — they must contact you."
            value={p.reschedule_window_hours}
            onChange={(v) => setP({ ...p, reschedule_window_hours: v })}
            min={0} max={168} suffix="hours"
            testid="resched-window-input"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Fees & deposits</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <NumberField
            label="Late-cancel fee"
            hint="Charged when a client cancels inside the notice window."
            value={p.late_cancel_fee_pct}
            onChange={(v) => setP({ ...p, late_cancel_fee_pct: v })}
            min={0} max={100} suffix="% of price"
            testid="late-fee-input"
          />
          <NumberField
            label="No-show fee"
            hint="Charged when a client doesn't show and doesn't cancel."
            value={p.no_show_fee_pct}
            onChange={(v) => setP({ ...p, no_show_fee_pct: v })}
            min={0} max={100} suffix="% of price"
            testid="no-show-fee-input"
          />
          <NumberField
            label="Deposit at booking"
            hint="Collected at time of booking (needs Stripe — coming next)."
            value={p.deposit_pct}
            onChange={(v) => setP({ ...p, deposit_pct: v })}
            min={0} max={100} suffix="% of price"
            testid="deposit-input"
          />
          <div className="flex items-end pb-1">
            <ToggleField
              label="Require deposit before slot is held"
              hint="If off, the deposit is collected but the slot is still confirmed if payment fails."
              checked={p.deposit_required}
              onChange={(v) => setP({ ...p, deposit_required: v })}
              testid="toggle-deposit-required"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Waitlist</CardTitle>
        </CardHeader>
        <CardContent>
          <ToggleField
            label="Auto-offer freed slots to the waitlist"
            hint="When a booking cancels, we email the next matching waitlist entry so they can grab it."
            checked={p.auto_fill_waitlist}
            onChange={(v) => setP({ ...p, auto_fill_waitlist: v })}
            testid="toggle-autofill"
          />
          <NumberField
            label="Waitlist response window"
            hint="How long a waitlisted client has to claim the offered slot before we offer it to the next person."
            value={p.waitlist_response_minutes}
            onChange={(v) => setP({ ...p, waitlist_response_minutes: v })}
            min={5} max={1440} suffix="minutes"
            testid="waitlist-response-input"
          />
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-policies-btn" className="shadow-lg">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save policies
        </Button>
      </div>
    </div>
  );
}
